import { General } from "../../../siteManagement/general";
import { SheetEventType } from "../../../siteManagement/sheet";
import { Metadata } from "../../core/components/commonApp";
import { KendoTree } from "../../core/components/kendoTree";
import { ToolBar, ToolBarStandardButton, ToolBarStandardButtonType } from "../../core/components/toolBar";
import { Config } from "../../core/config/config";
import { DialogHelper } from "../../core/util/dialogHelper";
import { Helper } from "../../core/util/helper";
import { HTTPService, JsonResultClass } from "../../core/util/httpManager";
import { NotificationHelper } from "../../core/util/notificationHelper";
import { DocumentFolderStructure, DocumentFolderStructureTreeVm } from "../../documents/documents";
import { DocumentFolderTemplate } from "./editDocumentFolderStructure";
import EditDocumentFolderStructureTreeItem, { EditDocumentFolderStructureTreeItemProps } from "./editDocumentFolderStructureTreeItem";
import React from "react";
export interface EditDocumentFolderStructureTreeProps {
    documentFolderTemplate?: DocumentFolderTemplate;
}

export default class EditDocumentFolderStructureTree extends React.Component<EditDocumentFolderStructureTreeProps, {
    selectedNode: DocumentFolderStructure;
    vm: DocumentFolderStructureTreeVm;
}>{
    constructor(props) {
        super(props);
        this.state = {
            selectedNode: null,
            vm: null
        }
    }

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.documentFolderTemplate?.EntityId != prevProps.documentFolderTemplate?.EntityId) {
            this.setState({ selectedNode: null }, this.loadData);
        }
    }

    private loadData = async () => {
        let result = await HTTPService.get<JsonResultClass<DocumentFolderStructureTreeVm>>("GeneralSettingsDocumentsFolderStructure/GetFolderStructure?entityId=" + encodeURIComponent(this.props.documentFolderTemplate?.EntityId));
        if (result.Code == 0) {
            this.setState({ vm: result.Data });
        } else {
            NotificationHelper.showErrorMessage(result.Message);
        }
    }

    private getButtons() {
        let hasSelectedNode = Helper.hasValue(this.state.selectedNode);
        let standardButtons: Array<ToolBarStandardButton> = [];
        standardButtons.push({ type: ToolBarStandardButtonType.NEW, caption: Config.translations.newLabel, enabled: hasSelectedNode });
        standardButtons.push({ type: ToolBarStandardButtonType.EDIT, caption: Config.translations.editLabel, enabled: hasSelectedNode && !this.state.selectedNode.IsRootFolder });
        standardButtons.push({ type: ToolBarStandardButtonType.DELETE, caption: Config.translations.deleteLabel, enabled: hasSelectedNode && this.state.selectedNode.Children.length <= 0 && !this.state.selectedNode.IsRootFolder });
        return standardButtons
    }

    private onButtonClick = (type: ToolBarStandardButtonType) => {
        switch (type) {
            case ToolBarStandardButtonType.NEW:
                let entity = this.createNewEntity();
                this.openEditDocumentFolderStructureTreeItem(entity);
                break;
            case ToolBarStandardButtonType.EDIT:
                if (Helper.hasValue(this.state.selectedNode))
                    this.openEditDocumentFolderStructureTreeItem(this.state.selectedNode);
                break;

            case ToolBarStandardButtonType.DELETE:
                if (Helper.hasValue(this.state.selectedNode))
                    this.deleteDocumentFolderStructureItem(this.state.selectedNode);
                break;
        }
    }

    private deleteDocumentFolderStructureItem = async (dataItem: DocumentFolderStructure) => {
        let isOk = await DialogHelper.confirm(Config.translations.deleteMessage);
        if (isOk) {
            let result = await HTTPService.post<JsonResultClass>('GeneralSettingsDocumentsFolderStructure/DeleteFolderStructureItem?id=' + encodeURIComponent(dataItem.Id))
            if (result.Code == 0) {
                this.setState({ selectedNode: null }, this.loadData);
            }
            else {
                NotificationHelper.showErrorMessage(result.Message);
            }
        }
    }

    private createNewEntity = () => {
        let entity: DocumentFolderStructure = {
            Id: kendo.guid().toString(),
            TranslationsLoaded: true,
            Translations: [],
            ParentId: this.state.selectedNode.IsRootFolder ? null : this.state.selectedNode.Id,
            TemplateId: this.props.documentFolderTemplate?.EntityId,
            IsNew: true,
            IsWatchable: true,
            IsWatched: true,
            RecordRight: { CanRead: true, CanWrite: true }
        };

        return entity;
    }

    private openEditDocumentFolderStructureTreeItem = (dataItem: DocumentFolderStructure) => {
        HTTPService.chainBatch
            .add<Metadata>(Helper.getMetaData("editDocumentFolderStructureTreeItem"))
            .promise()
            .then(result => {
                const metadata = result[0];
                this.initWindow(dataItem, metadata);
            });
    }

    private initWindow = (entity: DocumentFolderStructure, metaData: any) => {
        entity = Object.assign(entity, {
            RecordRight: {
                CanRead: true,
                CanWrite: true
            }
        })

        const props: EditDocumentFolderStructureTreeItemProps = {
            dataSources: {},
            metadata: metaData as Metadata,
            entity: entity,
            isWindow: true,
            context: {},
            title: ""
        }
        const sheet = General.Sheet.Manager.createSheet();
        sheet.bind(SheetEventType.Close, () => {
            this.loadData();
        });
        sheet.show(() => {
            sheet.mountContent(EditDocumentFolderStructureTreeItem, props);
        });
    }

    private getTemplate(dataItem) {
        let item: DocumentFolderStructure = dataItem.item;

        let text = item.TranslatedDescription;
        let typIcon = `<i class='fal fa-folder' title='${Helper.getTranslation("WebSMS.Skills.Folder")}' />`;

        return `<div style='display: flex;'>
                    <div>${typIcon}</div>
                    <label style='margin-left: 5px; display: inline-block; margin-right: 5px; white-space: nowrap; text-overflow: ellipsis; overflow: hidden; width: 200px;' title='${text}'>
                        ${text}
                    </label> 
                </div>`;
    }

    private getKendoTree = () => {
        return <KendoTree treeData={this.state.vm?.TreeData}
            template={(dataItem) => this.getTemplate(dataItem)}
            refreshOnTreeDataChanged={true}
            showExpandAndCollapseIcons={true}
            onSelectionChange={(dataItem) => this.setState({ selectedNode: dataItem })} />
    }

    private getToolBar = () => {
        return (
            <div style={{ margin: "5px", display: "inline-flex", alignItems: "center" }}>
                <ToolBar standardButtons={this.getButtons()} handleStandardButtonClick={this.onButtonClick} />
            </div>);
    }

    render() {
        return (<>
            {this.getToolBar()}
            {this.getKendoTree()}
        </>);
    }
}